<script setup lang="ts">
import ComboboxCommand from "@/components/ComboboxCommand.vue";
import DeleteButton from "@/components/DeleteButton.vue";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import ListItem from "@/components/ListItem.vue";
import { useGetUsers } from "@/users/usersCrud";
import { Trash } from "@iconoir/vue";
import {
  useAddParticipant,
  useConfirmPendingParticipant,
  useDeleteParticipant,
  useDeletePendingParticipant,
  useListPendingParticipants,
} from "./canvassesCrud";
import type { Canvass } from "./types";
import { useParticipation } from "./useParticipation";

const { canvass } = defineProps<{
  canvass: Canvass;
}>();

const { addParticipant } = useAddParticipant(() => canvass);
const { deleteParticipant, deleteParticipantStatus, resetDeleteParticipant } =
  useDeleteParticipant(() => canvass);

const { listPendingParticipantsStatus, pendingParticipants } =
  useListPendingParticipants(canvass, { enablePolling: true });
const { confirmPendingParticipant, confirmPendingParticipantStatus } =
  useConfirmPendingParticipant(canvass);
const {
  deletePendingParticipant,
  deletePendingParticipantStatus,
  resetDeletePendingParticipant,
} = useDeletePendingParticipant(canvass);

const { users, getUsersStatus } = useGetUsers();

const { assignedUsers, unassignedUsers } = useParticipation(
  () => canvass,
  users,
);
</script>

<template>
  <div class="multi-columns">
    <div class="column">
      <HandleQueryStatus :query-status="listPendingParticipantsStatus">
        <section class="box">
          <h2>Offene Einladungen</h2>
          <ul role="list">
            <ListItem
              v-for="pendingParticipant in pendingParticipants"
              :key="pendingParticipant.id"
            >
              <template #header>
                <h3>
                  {{ pendingParticipant.name }}
                </h3>
              </template>
              <p>
                {{ pendingParticipant.email }}
              </p>
              <template #actions>
                <button
                  class="button"
                  :disabled="confirmPendingParticipantStatus === 'pending'"
                  :aria-busy="confirmPendingParticipantStatus === 'pending'"
                  @click="confirmPendingParticipant(pendingParticipant)"
                >
                  Bestätigen
                </button>
                <DeleteButton
                  class="button button--tertiary"
                  :deleting-status="deletePendingParticipantStatus"
                  @confirmed="deletePendingParticipant(pendingParticipant)"
                  @initiated="resetDeletePendingParticipant"
                >
                  <Trash />
                  <span class="visually-hidden"> Löschen </span>
                  <template #confirmation>
                    Möchtest du die Teilnahmeanfrage wirklich löschen?
                  </template>
                </DeleteButton>
              </template>
            </ListItem>
          </ul>
        </section>
      </HandleQueryStatus>
    </div>
    <div class="column">
      <HandleQueryStatus :query-status="getUsersStatus">
        <section class="box">
          <h2>Teilnehmer:innen hinzufügen</h2>
          <ComboboxCommand
            :items="unassignedUsers"
            search-label="Nutzer:innen suchen..."
            empty-search-result-label="Keine Nutzerinnen:innen gefunden"
            @select="addParticipant"
          />
        </section>
        <section class="box">
          <h2>Bestätigte Teilnehmer:innen</h2>
          <ul role="list">
            <ListItem v-for="user in assignedUsers" :key="user.id">
              <template #header>
                <h3>{{ user.name }}</h3>
              </template>
              <dl>
                <dt class="visually-hidden">E-Mail</dt>
                <dd>{{ user.email }}</dd>
                <dt class="visually-hidden">Rolle</dt>
                <dd>{{ user.role }}</dd>
              </dl>
              <template #actions>
                <DeleteButton
                  class="button button--tertiary"
                  :deleting-status="deleteParticipantStatus"
                  @confirmed="deleteParticipant(user)"
                  @initiated="resetDeleteParticipant"
                >
                  <Trash />
                  <span class="visually-hidden"> Löschen </span>
                  <template #confirmation>
                    Möchtest du die Teilnahme von
                    {{ user.name }} wirklich löschen?
                  </template>
                </DeleteButton>
              </template>
            </ListItem>
          </ul>
        </section>
      </HandleQueryStatus>
    </div>
  </div>
</template>

<style scoped>
p,
dd {
  color: var(--text-color-muted);
}
</style>
